import { createTheme } from '@mui/material';
// types `components.MuiPickersOutlinedInput` below
import type {} from '@mui/x-date-pickers/themeAugmentation';

/**
 * Colours the design system names that MUI's palette has no slot for. Declared here so
 * `theme.palette.field.border` and `sx={{ borderColor: 'field.border' }}` are typed.
 */
declare module '@mui/material/styles' {
  interface DesignTokens {
    /** Page and card backgrounds: white, the table header grey, the brand tint, row hover. */
    surface: { main: string; subtle: string; tint: string; hover: string };
    /** Dividers and the outline of anything that is a frame rather than a field. */
    line: string;
    /** Outline of an input: resting and hover. Focus is `primary`, 2px. `disabled` is dashed. */
    field: { border: string; hover: string; strong: string; disabled: string };
    /** The navigation sidebar, which is primary-coloured but is not "primary" as an action. */
    sidebar: { main: string; selected: string; textMuted: string };
    /** "Still to do": the same orange as `warning.main`, for text and icons that are not a status. */
    attention: string;
    /** `mix`: the four slices of a composition chart, biggest first. */
    chart: { charged: string; mix: string[] };
    lavender: { main: string; soft: string; ink: string };
    /** Backgrounds behind an info, warning or error message. */
    tint: { info: string; warning: string; error: string };
    brand: { mark: string };
    /** The dot beside an active location, person or group; never used for text. */
    statusDot: { active: string };
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Palette extends DesignTokens {}
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface PaletteOptions extends Partial<DesignTokens> {}
  interface TypeText {
    /** A section heading inside a card, quieter than a title. */
    section: string;
    placeholder: string;
    /**
     * A value that is not there yet, e.g. an untranslated title. Not `text.disabled`: MUI
     * uses that for disabled buttons and inputs, and #d9d9d9 there would make them vanish.
     */
    faint: string;
    link: string;
    /** "Active", "Translated": success said in words, dark enough to read (5.23:1). */
    successText: string;
    warningText: string;
  }
  interface PaletteColor {
    /** A border that carries the colour's meaning, e.g. a field flagged for attention. */
    border?: string;
  }
  interface SimplePaletteColorOptions {
    border?: string;
  }
}

/**
 * Input outline, by contrast against white. The old #dfdcdb (1.36:1) all but vanished,
 * and a focus ring lighter than the resting border did not read as focus at all.
 */
const FIELD = { border: '#948EA3' /* 3.15:1 */, hover: '#7D7690' /* 4.32:1 */, strong: '#948EA3', disabled: '#C0C0C0' };
const TEXT_SECONDARY = '#626262';
const SURFACE_HOVER = '#F5F5F5';

/**
 * A disabled field: grey, with a dashed outline, and its value still readable. MUI's own
 * leaves it white with an outline as strong as an active field's, so a field that cannot
 * be typed in looked like an empty one waiting for input.
 */
const DISABLED_FIELD = {
  backgroundColor: SURFACE_HOVER,
  cursor: 'not-allowed'
};
const DISABLED_VALUE = { color: TEXT_SECONDARY, WebkitTextFillColor: TEXT_SECONDARY, cursor: 'not-allowed' };
const PRIMARY = '#3F3273';
const ERROR = '#D32F2F';
const WARNING = '#B45309';

export const theme = createTheme({
  palette: {
    primary: {
      main: PRIMARY,
      light: '#9747FF'
    },
    text: {
      primary: '#483359',
      secondary: TEXT_SECONDARY,
      section: '#6B6B6B',
      placeholder: '#9E9E9E',
      faint: '#D9D9D9',
      link: '#3F749C',
      successText: '#5A7444',
      warningText: '#B28510'
    },
    // statuses, chosen for contrast on white: MUI's default warning (#ed6c02, 3.11:1)
    // was too faint for the "Draft" and "Partly paid" chips
    success: {
      main: '#2E7D32' // 5.13:1
    },
    warning: {
      main: WARNING, // 5.02:1
      border: '#F1C40F'
    },
    info: {
      main: '#0288D1'
    },
    error: {
      main: ERROR,
      // the delete button's hover
      dark: '#B71C1C'
    },
    background: {
      paper: '#ffffff'
    },
    surface: { main: '#FFFFFF', subtle: '#FBFBFC', tint: '#F5F5FE', hover: SURFACE_HOVER },
    line: '#F0F0F0',
    field: FIELD,
    sidebar: { main: PRIMARY, selected: '#524086', textMuted: '#B5BCC4' },
    attention: WARNING,
    chart: { charged: '#546E7A', mix: [PRIMARY, '#786CB2', '#C0B6E0', '#DDDAEB'] },
    lavender: { main: '#C0B6E0', soft: '#DDDAEB', ink: '#BEBAD4' },
    tint: { info: '#E5F3FE', warning: '#FFF5D8', error: '#FFE8E8' },
    brand: { mark: '#786CB2' },
    statusDot: { active: '#97B66B' }
  },
  typography: {
    fontFamily: '"Poppins", sans-serif',
    allVariants: {
      color: '#483359'
    },
    body1: {
      fontSize: 16,
      fontWeight: 400
    },
    body2: {
      color: '#626262',
      fontSize: 14
    },
    h1: {
      fontSize: 28,
      fontWeight: 600
    },
    h2: {
      fontSize: 24,
      fontWeight: 500
    },
    caption: {
      fontWeight: 600,
      fontSize: 16
    },
    subtitle1: {
      fontWeight: 600,
      color: 'white'
    },
    h5: {
      fontSize: 14,
      fontWeight: 600
    }
  },

  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 8,
          '&.Mui-selected': {
            backgroundColor: '#524086',
            '&:hover': {
              backgroundColor: '#524086'
            }
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          width: 280,
          backgroundColor: '#3F3273',
          color: '#ffffff',
          borderRight: 'none'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          backgroundColor: '#ffffff',
          paddingRight: 1,
          // not on a disabled, errored or focused field: those keep their own outline
          '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: FIELD.hover,
            borderWidth: 1
          },
          // 2px primary (10.97:1): focus must be stronger than rest, never lighter
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: PRIMARY,
            borderWidth: 2
          },
          // 1px plus a 1px inset: reads as 2px without the field moving by a pixel
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: ERROR,
            boxShadow: `inset 0 0 0 1px ${ERROR}`
          },
          // focus already draws 2px; the inset on top would make it 3
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: 'none'
          },
          // three classes, so it wins over a field's own white background set at two
          '&.MuiOutlinedInput-root.Mui-disabled': {
            ...DISABLED_FIELD,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD.disabled, borderStyle: 'dashed', borderWidth: 1 },
            '& .MuiOutlinedInput-input, & .MuiSelect-select': DISABLED_VALUE
          }
        },
        notchedOutline: {
          borderColor: FIELD.border,
          borderWidth: 1
        }
      }
    },
    // date fields in @mui/x-date-pickers v8 are their own input, not MuiOutlinedInput; same
    // outline, so a date does not look lighter than the text field beside it. Focus (2px
    // primary), error and disabled are already right there.
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused) .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: FIELD.hover
          },
          '&.Mui-error:not(.Mui-focused) .MuiPickersOutlinedInput-notchedOutline': {
            boxShadow: `inset 0 0 0 1px ${ERROR}`
          },
          '&.MuiPickersOutlinedInput-root.Mui-disabled': {
            ...DISABLED_FIELD,
            '& .MuiPickersOutlinedInput-notchedOutline': { borderColor: FIELD.disabled, borderStyle: 'dashed', borderWidth: 1 },
            '& .MuiPickersSectionList-root, & .MuiPickersSectionList-sectionContent, & input': DISABLED_VALUE
          }
        },
        notchedOutline: {
          borderColor: FIELD.border
        }
      }
    },
    // the label of a disabled field stays readable, in the secondary text colour
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': { color: TEXT_SECONDARY }
        }
      }
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: '#9E9E9E'
        }
      }
    },
    /**
     * MUI builds FormHelperText from the `caption` variant, which is deliberately
     * 16px/600 here for tab labels and field captions. Inherited unchanged, a hint
     * under an input came out heavier than the label it belongs to.
     *
     * Overridden here rather than by changing `caption`, which the tabs and several
     * labels rely on.
     */
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.4,
          color: '#626262',
          marginTop: 4
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        h6: {
          width: '100%',
          fontWeight: 400,
          fontSize: 20,
          lineHeight: '30px',
          letterSpacing: '0%',
          marginBottom: '16px',
          color: '#483359'
        }
      }
    },
    // tab labels in sentence case, as every other label in the app
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'black',
          color: 'white',
          fontSize: 14,
          textAlign: 'center'
        }
      }
    }
  }
});

/** The palette for code outside `sx` and `styled`: chart series, inline SVG styles, toasts. */
export const palette = theme.palette;
